import React, { useEffect, useRef, useState } from 'react';
import { searchChat } from '../lib/chatLink';

/**
 * Search across every chat (migration 079).
 *
 * Gordon, 23 October: search all chats by keyword - what was said and the
 * names of files - with a filter by person and by conversation; each result
 * shows the match highlighted, the conversation, who said it and when, and
 * a click lands on the message in its conversation.
 *
 * It takes the place of the list down the side while it is open, as Slack's
 * does, and the list is one Close (or Escape) away. Results stay put while
 * you open them one after another, so a phone's Back from a conversation
 * comes back to them.
 *
 * WHAT CAN BE FOUND is the server's business, never this page's: it searches
 * only conversations the person may open (backend lib/chat-search.js). The
 * highlights arrive as ranges into plain text and are drawn as <mark>s here -
 * no markup from the server is ever put into the page.
 */

/** Text with the given [start, end] ranges marked. */
export const Marked = ({ text, ranges }) => {
  const s = String(text || '');
  const parts = [];
  let at = 0;
  for (const [a, b] of (ranges || []).filter((r) => Array.isArray(r) && r[0] >= 0 && r[1] <= s.length)) {
    if (a < at) continue;
    if (a > at) parts.push(s.slice(at, a));
    parts.push(<mark key={a} className="ch-hit">{s.slice(a, b)}</mark>);
    at = b;
  }
  if (at < s.length) parts.push(s.slice(at));
  return <>{parts}</>;
};

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};

/** Where a result was said, in words: "#Office move · Packing", "Rita Gatt · Billing", "UPG-1 Printer". */
export const whereOf = (r) => {
  const c = r.conversation || {};
  // A reply in a branch (084) says so: it opens the branch, on the reply.
  const branch = r.reply_to_id ? ' · in a branch' : '';
  if (c.kind === 'group') return `#${c.name || 'Group'}${c.topic ? ` · ${c.topic}` : ''}${branch}`;
  if (c.kind === 'ticket') return `${c.ticket_number || 'Ticket'}${c.title ? ` ${c.title}` : ''}`;
  const name = c.with?.is_me ? 'Just you' : (c.with?.name || 'Somebody');
  return `${name}${c.topic ? ` · ${c.topic}` : ''}${branch}`;
};

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
    <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d="M10.4 10.4L14 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
export { SearchIcon };

// initialQuery: the words of the global search's "See all in Chat" (?q= on /chat).
const ChatSearch = ({ people, groups, onOpen, onClose, current, initialQuery = '' }) => {
  const [q, setQ] = useState(initialQuery);
  const [from, setFrom] = useState('');
  const [where, setWhere] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const input = useRef(null);
  const asked = useRef(0);

  useEffect(() => { input.current?.focus({ preventScroll: true }); }, []);

  const run = async (offset = 0) => {
    const words = q.trim();
    if (words.length < 2) { setFound(null); setError(null); return; }
    const n = asked.current + 1;
    asked.current = n;
    setBusy(true);
    setError(null);
    try {
      const got = await searchChat({
        q: words, limit: 20, offset,
        ...(from ? { from } : {}),
        ...(where ? { in: where } : {})
      });
      if (asked.current !== n) return;
      setFound((was) => ({
        results: offset > 0 && was ? [...was.results, ...(got?.results || [])] : (got?.results || []),
        next: got?.next_offset ?? null
      }));
    } catch (err) {
      if (asked.current !== n) return;
      setError(err.response?.status === 503
        ? (err.response?.data?.error || 'Search is not set up on this server yet.')
        : (err.response?.data?.error || 'Could not search just now.'));
    } finally {
      if (asked.current === n) setBusy(false);
    }
  };

  // As you type, after a pause - and at once when a filter changes.
  useEffect(() => {
    const t = setTimeout(() => run(0), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, from, where]);

  const others = (people || []).filter((p) => p.key);
  const results = found?.results || [];

  return (
    <div className="ch-search" role="search">
      <div className="ch-search-bar">
        <span className="ch-search-icon" aria-hidden="true"><SearchIcon /></span>
        <input
          ref={input}
          type="search"
          className="ch-field ch-search-input"
          placeholder="Search messages and files…"
          aria-label="Search messages and files"
          value={q}
          maxLength={200}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            if (e.key === 'Enter') { e.preventDefault(); run(0); }
          }}
        />
        <button type="button" className="ch-flat ch-small ch-search-close" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="ch-search-filters">
        <label className="ch-search-filter">
          <span className="ch-say-label">From</span>
          <select className="ch-field" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From">
            <option value="">From anyone</option>
            {others.map((p) => (
              <option key={p.key} value={p.key}>{p.is_me ? 'You' : (p.name || p.email)}</option>
            ))}
          </select>
        </label>
        <label className="ch-search-filter">
          <span className="ch-say-label">In</span>
          <select className="ch-field" value={where} onChange={(e) => setWhere(e.target.value)} aria-label="In">
            <option value="">In all conversations</option>
            {/* 096: what is in a branch marked answered - its message and its replies. */}
            <option value="answered">Answered branches</option>
            {(groups || []).length > 0 && (
              <optgroup label="Groups">
                {groups.map((g) => <option key={g.id} value={`group:${g.id}`}>#{g.name}</option>)}
              </optgroup>
            )}
            {others.length > 0 && (
              <optgroup label="People">
                {others.map((p) => (
                  <option key={p.key} value={`person:${p.key}`}>{p.is_me ? 'Just you' : (p.name || p.email)}</option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
      </div>

      <p className="ch-search-status" aria-live="polite">
        {error || (busy && !found ? 'Searching…'
          : q.trim().length < 2 ? 'Type at least two letters. Every word has to be in the message or the file name.'
            : found && results.length === 0 ? 'Nothing matches. Try fewer words, or another person or conversation.'
              : found ? `${results.length}${found.next !== null ? '+' : ''} result${results.length === 1 ? '' : 's'}` : '')}
      </p>

      {results.length > 0 && (
        <ul className="ch-list ch-search-results">
          {results.map((r) => {
            const here = current && current === r.message_id;
            return (
              <li key={`${r.kind}-${r.message_id}`}>
                <button
                  type="button"
                  className="ch-result"
                  aria-current={here ? 'true' : undefined}
                  onClick={() => onOpen(r)}
                >
                  <span className="ch-result-top">
                    <span className="ch-result-where" data-kind={r.kind}>{whereOf(r)}</span>
                    <time className="ch-result-when" dateTime={r.at}>{when(r.at)}</time>
                  </span>
                  <span className="ch-result-text">
                    <span className="ch-result-who">{r.mine ? 'You' : r.author_name}: </span>
                    <Marked text={r.snippet} ranges={r.highlights} />
                  </span>
                  {r.file_name && (
                    <span className="ch-result-file">
                      <span aria-hidden="true">📎 </span>
                      <Marked text={r.file_name} ranges={r.file_highlights} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {found?.next !== null && found?.next !== undefined && (
        <button type="button" className="ch-flat ch-search-more" disabled={busy} onClick={() => run(found.next)}>
          {busy ? 'Loading…' : 'More results'}
        </button>
      )}
    </div>
  );
};

export default ChatSearch;
